export const GROWTH_ANALYTICS_PERIODS = [
  { days: 7, label: "۷ روز", enabled: true },
  { days: 30, label: "۳۰ روز", enabled: true },
  { days: 90, label: "۹۰ روز", enabled: true },
  { days: 180, label: "۶ ماه", enabled: true },
  { days: 365, label: "۱۲ ماه", enabled: true },
];

/** 1=Daily, 2=Weekly, 3=Monthly (matches API GrowthAnalyticsAggregation) */
export const GROWTH_ANALYTICS_AGGREGATIONS = [
  { value: 1, label: "روزانه" },
  { value: 2, label: "هفتگی" },
  { value: 3, label: "مقایسه ماهانه" },
];

export const DEFAULT_GROWTH_PERIOD_DAYS = 30;
export const DEFAULT_GROWTH_AGGREGATION = 1;
export const MONTHLY_COMPARE_AGGREGATION = 3;


export const EMPTY_GROWTH_ANALYTICS = {
  periodDays: DEFAULT_GROWTH_PERIOD_DAYS,
  aggregation: DEFAULT_GROWTH_AGGREGATION,
  summary: {
    totalNewUsers: 0,
    totalRequests: 0,
    averageConversionRate: 0,
  },
  trend: [],
};

const pickField = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const normalizeTrendPoint = (point) => ({
  date: pickField(point, "date", "Date") || "",
  newUsersCount: Number(
    pickField(point, "newUsersCount", "NewUsersCount") ?? 0
  ),
  powerPlantRequestsCount: Number(
    pickField(point, "powerPlantRequestsCount", "PowerPlantRequestsCount") ?? 0
  ),
  conversionRate: Number(
    pickField(point, "conversionRate", "ConversionRate") ?? 0
  ),
});

/** استخراج growthAnalytics از پاسخ GetAnalytics یا GetTrend (camelCase / PascalCase). */
export const extractGrowthAnalyticsPayload = (data) => {
  if (!data) return null;

  const nested = pickField(data, "growthAnalytics", "GrowthAnalytics");
  if (nested) return nested;

  if (
    pickField(data, "trend", "Trend", "summary", "Summary") !== undefined
  ) {
    return data;
  }

  return null;
};

export const normalizeGrowthAnalytics = (data) => {
  const source = extractGrowthAnalyticsPayload(data) || data || {};
  const summary = pickField(source, "summary", "Summary") || {};
  const trendRaw =
    pickField(source, "trend", "Trend", "dailyGrowthTrend", "DailyGrowthTrend") ||
    [];

  return {
    ...EMPTY_GROWTH_ANALYTICS,
    periodDays: Number(
      pickField(source, "periodDays", "PeriodDays") ?? DEFAULT_GROWTH_PERIOD_DAYS
    ),
    aggregation: Number(
      pickField(source, "aggregation", "Aggregation") ?? DEFAULT_GROWTH_AGGREGATION
    ),
    summary: {
      totalNewUsers: Number(
        pickField(summary, "totalNewUsers", "TotalNewUsers") ?? 0
      ),
      totalRequests: Number(
        pickField(summary, "totalRequests", "TotalRequests") ?? 0
      ),
      averageConversionRate: Number(
        pickField(summary, "averageConversionRate", "AverageConversionRate") ?? 0
      ),
    },
    trend: Array.isArray(trendRaw) ? trendRaw.map(normalizeTrendPoint) : [],
  };
};

export const hasGrowthAnalyticsData = (analytics) => {
  const normalized = normalizeGrowthAnalytics(analytics);
  if (
    (normalized.summary?.totalNewUsers || 0) > 0 ||
    (normalized.summary?.totalRequests || 0) > 0
  ) {
    return true;
  }

  return (
    Array.isArray(normalized.trend) &&
    normalized.trend.some(
      (item) =>
        (item?.newUsersCount || 0) > 0 ||
        (item?.powerPlantRequestsCount || 0) > 0
    )
  );
};

export const normalizeSmsSummary = (data) => {
  const source = pickField(data, "smsSummary", "SmsSummary") || data || {};
  const monthsRaw = pickField(source, "recentMonths", "RecentMonths") || [];

  return {
    persianMonthLabel: pickField(source, "persianMonthLabel", "PersianMonthLabel"),
    monthlySmsCount: Number(
      pickField(source, "monthlySmsCount", "MonthlySmsCount") ?? 0
    ),
    monthlySmsCost: Number(
      pickField(source, "monthlySmsCost", "MonthlySmsCost") ?? 0
    ),
    remainingCredit: pickField(source, "remainingCredit", "RemainingCredit"),
    accountType: pickField(source, "accountType", "AccountType"),
    creditAvailable: Boolean(
      pickField(source, "creditAvailable", "CreditAvailable")
    ),
    creditError: pickField(source, "creditError", "CreditError"),
    recentMonths: Array.isArray(monthsRaw)
      ? monthsRaw.map((item) => ({
          monthLabel: pickField(item, "monthLabel", "MonthLabel") || "",
          smsCount: Number(pickField(item, "smsCount", "SmsCount") ?? 0),
          totalCost: Number(pickField(item, "totalCost", "TotalCost") ?? 0),
        }))
      : [],
  };
};

export const buildSmsMonthlyChartData = (summary) =>
  (summary?.recentMonths || []).map((item) => ({
    x: item.monthLabel,
    هزینه: item.totalCost,
    تعداد: item.smsCount,
  }));

export const hasSmsUsageInChart = (summary) =>
  (summary?.recentMonths || []).some(
    (item) => (item?.totalCost || 0) > 0 || (item?.smsCount || 0) > 0
  );

export const normalizeOverview = (data) => {
  const source = pickField(data, "overview", "Overview") || data || {};
  const trendRaw =
    pickField(source, "monthlyEnergyTrend", "MonthlyEnergyTrend") || [];

  return {
    totalUsers: Number(pickField(source, "totalUsers", "TotalUsers") ?? 0),
    totalRequests: Number(
      pickField(source, "totalRequests", "TotalRequests") ?? 0
    ),
    operatingPlants: Number(
      pickField(source, "operatingPlants", "OperatingPlants") ?? 0
    ),
    activeContractors: Number(
      pickField(source, "activeContractors", "ActiveContractors") ?? 0
    ),
    totalEnergyProducedKwh: Number(
      pickField(source, "totalEnergyProducedKwh", "TotalEnergyProducedKwh") ?? 0
    ),
    hasEnergyData: Boolean(
      pickField(source, "hasEnergyData", "HasEnergyData")
    ),
    monthlyEnergyTrend: Array.isArray(trendRaw)
      ? trendRaw.map((item) => ({
          monthLabel: pickField(item, "monthLabel", "MonthLabel") || "",
          energyKwh: Number(
            pickField(item, "energyKwh", "EnergyKwh") ?? 0
          ),
        }))
      : [],
  };
};

export const buildMonthlyEnergyChartData = (overview) =>
  (overview?.monthlyEnergyTrend || []).map((item) => ({
    x: item.monthLabel,
    انرژی: item.energyKwh,
  }));
